type BackdropIsolationEntry = { count: number, ariaHidden: string | null, inert: boolean }
type OpenModalEntry = {
  id: symbol
  overlay: HTMLElement
  originalAriaHidden: string | null
  originalInert: boolean
}

const backdropIsolationByElement = new WeakMap<HTMLElement, BackdropIsolationEntry>()
const openModalEntries: OpenModalEntry[] = []
let originalBodyOverflow = ''
let originalDocumentOverflow = ''

function modalZIndex(entry: OpenModalEntry): number {
  const parsed = Number.parseInt(window.getComputedStyle(entry.overlay).zIndex, 10)
  return Number.isFinite(parsed) ? parsed : 0
}

function isLaterInDocument(left: HTMLElement, right: HTMLElement): boolean {
  return Boolean(left.compareDocumentPosition(right) & Node.DOCUMENT_POSITION_FOLLOWING)
}

function topmostModalEntry(): OpenModalEntry | undefined {
  return openModalEntries.reduce<OpenModalEntry | undefined>((topmost, entry) => {
    if (!topmost) return entry
    const entryZIndex = modalZIndex(entry)
    const topmostZIndex = modalZIndex(topmost)
    if (entryZIndex !== topmostZIndex) return entryZIndex > topmostZIndex ? entry : topmost
    return isLaterInDocument(topmost.overlay, entry.overlay) ? entry : topmost
  }, undefined)
}

function updateAccessibility(): void {
  const topmost = topmostModalEntry()
  for (const entry of openModalEntries) {
    if (entry === topmost) {
      entry.overlay.inert = entry.originalInert
      if (entry.originalAriaHidden === null)
        entry.overlay.removeAttribute('aria-hidden')
      else
        entry.overlay.setAttribute('aria-hidden', entry.originalAriaHidden)
    } else {
      entry.overlay.inert = true
      entry.overlay.setAttribute('aria-hidden', 'true')
    }
  }
}

function preventBackgroundScroll(event: Event): void {
  const topmost = topmostModalEntry()
  const target = event.target
  if (!topmost || !(target instanceof Node) || topmost.overlay.contains(target)) return
  event.preventDefault()
}

function lockBackgroundScroll(): void {
  if (openModalEntries.length !== 1) return
  originalBodyOverflow = document.body.style.overflow
  originalDocumentOverflow = document.documentElement.style.overflow
  document.body.style.overflow = 'hidden'
  document.documentElement.style.overflow = 'hidden'
  document.addEventListener('wheel', preventBackgroundScroll, { capture: true, passive: false })
  document.addEventListener('touchmove', preventBackgroundScroll, { capture: true, passive: false })
}

function unlockBackgroundScroll(): void {
  if (openModalEntries.length > 0) return
  document.body.style.overflow = originalBodyOverflow
  document.documentElement.style.overflow = originalDocumentOverflow
  document.removeEventListener('wheel', preventBackgroundScroll, true)
  document.removeEventListener('touchmove', preventBackgroundScroll, true)
}

export function registerModal(id: symbol, overlay: HTMLElement): void {
  if (openModalEntries.some(entry => entry.id === id)) return
  openModalEntries.push({
    id,
    overlay,
    originalAriaHidden: overlay.getAttribute('aria-hidden'),
    originalInert: overlay.inert,
  })
  lockBackgroundScroll()
  updateAccessibility()
}

export function unregisterModal(id: symbol): void {
  const index = openModalEntries.findIndex(entry => entry.id === id)
  if (index < 0) return
  const [entry] = openModalEntries.splice(index, 1)
  if (entry) {
    entry.overlay.inert = entry.originalInert
    if (entry.originalAriaHidden === null)
      entry.overlay.removeAttribute('aria-hidden')
    else
      entry.overlay.setAttribute('aria-hidden', entry.originalAriaHidden)
  }
  updateAccessibility()
  unlockBackgroundScroll()
}

export function isTopmostModal(id: symbol): boolean {
  return topmostModalEntry()?.id === id
}

export function isolateElement(element: HTMLElement): void {
  let meta = backdropIsolationByElement.get(element)
  if (!meta) {
    meta = { count: 0, ariaHidden: element.getAttribute('aria-hidden'), inert: element.inert }
    backdropIsolationByElement.set(element, meta)
  }
  meta.count += 1
  element.setAttribute('aria-hidden', 'true')
  element.inert = true
}

export function restoreIsolatedElement(element: HTMLElement): void {
  const meta = backdropIsolationByElement.get(element)
  if (!meta) return
  meta.count -= 1
  if (meta.count > 0) return
  backdropIsolationByElement.delete(element)
  if (meta.ariaHidden === null)
    element.removeAttribute('aria-hidden')
  else
    element.setAttribute('aria-hidden', meta.ariaHidden)
  element.inert = meta.inert
}
